import React, { useMemo } from 'react'
import { theme } from 'theme'
import { Paper, Popper } from '@mui/material'
import { isOverflown } from 'particles/utils'
import { useStyles } from './TagCell'
import { GridAlignment, GridRenderCellParams } from '@mui/x-data-grid-pro'
import Text, { TextColor } from 'particles/Text'
import IconButton from 'particles/Button/IconButton'
import { IconName } from 'particles/Icon'

export interface StringProps {
    value?: JSX.Element | string
    color?: TextColor
    /**
     * isFullText allow to display full text (not wrap or elipsis),
     * used for dynamic row height value
     */
    isFullText?: boolean
    emptyDisplay?: string
}

interface StringCellGridRenderCellParams extends GridRenderCellParams {
    hasIcon?: boolean
    icon?: IconName
    onClick?: () => void
}

interface GridCellExpandProps {
    stringProps?: StringProps
    width?: number
    align?: GridAlignment
    hasIcon?: boolean
    icon?: IconName
    onClick?: () => void
}

const GridCellExpand = React.memo(function GridCellExpand(props: GridCellExpandProps) {
    const { width, stringProps, align, hasIcon, icon, onClick } = props
    const wrapper = React.useRef<HTMLDivElement | null>(null)
    const cellDiv = React.useRef(null)
    const cellValue = React.useRef<HTMLDivElement | null>(null)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const classes = useStyles()
    const [showFullCell, setShowFullCell] = React.useState(false)
    const [showPopper, setShowPopper] = React.useState(false)

    const handleMouseEnter = () => {
        const isCurrentlyOverflown = isOverflown(cellValue.current!)
        setShowPopper(isCurrentlyOverflown)
        setAnchorEl(cellDiv.current)
        setShowFullCell(true)
    }

    const handleMouseLeave = () => {
        setShowFullCell(false)
    }

    React.useEffect(() => {
        if (!showFullCell) {
            return undefined
        }

        function handleKeyDown(nativeEvent: KeyboardEvent) {
            // IE11, Edge (prior to using Bink?) use 'Esc'
            if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
                setShowFullCell(false)
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [setShowFullCell, showFullCell])

    const boxStyle = useMemo<React.CSSProperties>(() => {
        let justifyContent

        if (align === 'right') {
            justifyContent = 'flex-end'
        } else if (align === 'center') {
            justifyContent = 'center'
        }

        return { justifyContent }
    }, [align])

    const textColor = (() => {
        if (stringProps?.color) {
            return stringProps.color
        }

        if (!stringProps?.value) {
            return 'placeholder'
        }

        return 'primary'
    })()

    return (
        <div
            ref={wrapper}
            style={boxStyle}
            className={classes.root}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div
                ref={cellDiv}
                style={{
                    height: 1,
                    width,
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                }}
            />
            <Text
                ref={cellValue}
                color={textColor}
                scale="bodyM500"
                className="cellValue"
                style={{
                    textOverflow: 'ellipsis',
                    whiteSpace: stringProps?.isFullText ? 'initial' : 'nowrap',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                }}
            >
                {stringProps && stringProps.value ? stringProps.value : '-'}
            </Text>
            {hasIcon && icon ? (
                <IconButton
                    icon={icon}
                    form="icon"
                    color="neutral"
                    size="xs"
                    onClick={onClick}
                    style={{ position: 'absolute', right: 0 }}
                />
            ) : null}
            {showPopper && (
                <Popper
                    open={showFullCell && anchorEl !== null}
                    anchorEl={anchorEl}
                    style={{ width, marginLeft: -17, zIndex: 200 }}
                >
                    <Paper
                        elevation={1}
                        style={{
                            minHeight: wrapper.current!.offsetHeight - 3,
                            background: theme.newColors.neutral50,
                            width,
                            padding: 8,
                            boxShadow:
                                '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                        }}
                    >
                        <Text scale="bodyM500" color={textColor} style={{ wordBreak: 'break-all' }}>
                            {stringProps && stringProps.value ? stringProps.value : '-'}
                        </Text>
                    </Paper>
                </Popper>
            )}
        </div>
    )
})

export const StringCell = (params: StringCellGridRenderCellParams) => {
    const stringProps: StringProps = params.value as StringProps
    return (
        <GridCellExpand
            stringProps={stringProps}
            width={params.colDef.width}
            align={params.colDef.align}
            hasIcon={params.hasIcon}
            icon={params.icon}
            onClick={params.onClick}
        />
    )
}
